'use client'
import Link from 'next/link'
import {useCallback} from 'react'

import cn from '@/app/[locale]/(website)/components/utils/cn'
import {TreeNode} from '@/sanity/utils/treeNodes'

type TocItemProps = {
    element: TreeNode
    activeId: string | null
    handleClick: (slug: string) => void
    level?: number
}

export default function TocItem({element, activeId, handleClick, level = 1}: TocItemProps) {
    const {slug, text, children} = element
    const isActive = activeId === slug

    const handleLinkClick = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault()
            document.getElementById(slug)?.scrollIntoView({behavior: 'smooth'})
            handleClick(slug)
        },
        [slug, handleClick],
    )

    return (
        <li className="relative">
            <Link
                href={`#${slug}`}
                className={cn(
                    'block transition-colors duration-200 border-l-2 -ml-[2px]',
                    isActive
                        ? 'text-text-primary border-primary'
                        : 'text-text-quaternary hover:text-text-primary border-transparent',
                )}
                style={{paddingLeft: `${level * 8}px`}}
                onClick={handleLinkClick}
            >
                {text}
            </Link>
            {children.length > 0 && (
                <ul className="mt-1.5 space-y-2">
                    {children.map((child) => (
                        <TocItem
                            key={child.slug}
                            element={child}
                            activeId={activeId}
                            handleClick={handleClick}
                            level={level + 1}
                        />
                    ))}
                </ul>
            )}
        </li>
    )
}
